"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BoundaryFacts = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const ExpressionExpansion_1 = require("../ExpressionExpansion");
const SLOT_ORDER = ['min', 'typ', 'max'];
class BoundaryFacts {
    static { this.RELATIVE_EPSILON = 1e-9; }
    static fromBoundaries(bounds, signals) {
        const facts = [];
        for (const bound of bounds) {
            const definition = bound.boundary;
            if (!sigma_engine_shared_1.DesignModels.isBoundaryNumericalDefinition(definition)) {
                continue;
            }
            const { characteristic } = definition.numerical;
            if (!BoundaryFacts.isRated(characteristic)) {
                continue;
            }
            const [relative] = (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)([...signals], definition.relative_to);
            const targets = 'component' in definition.target ?
                [null]
                : (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)([...signals], definition.target).map(String);
            for (const signalId of targets) {
                for (const limit of BoundaryFacts.limits(definition.numerical)) {
                    facts.push({
                        signalId,
                        characteristic,
                        ...limit,
                        relativeSignalId: relative === undefined ? null : String(relative),
                        conditionsKey: BoundaryFacts.conditionsKey(bound),
                        level: definition.level ?? null,
                    });
                }
            }
        }
        return BoundaryFacts.sorted(facts);
    }
    static equal(a, b, signals) {
        return BoundaryFacts.factsEqual(BoundaryFacts.fromBoundaries(a, signals), BoundaryFacts.fromBoundaries(b, signals));
    }
    static factsEqual(a, b) {
        const left = BoundaryFacts.distinct(a);
        const right = BoundaryFacts.distinct(b);
        return (left.length === right.length && left.every((fact, index) => BoundaryFacts.sameFact(fact, right[index] ?? null)));
    }
    static apply(bounds, overrides) {
        let patched = bounds.map((bound) => structuredClone(bound));
        for (const override of overrides) {
            switch (override.op) {
                case ExpressionExpansion_1.BoundOverrideOp.Add: {
                    patched.push(structuredClone(override.bound));
                    break;
                }
                case ExpressionExpansion_1.BoundOverrideOp.Remove: {
                    patched = patched.filter((bound) => bound.id !== override.id);
                    break;
                }
                case ExpressionExpansion_1.BoundOverrideOp.Change: {
                    patched = patched.map((bound) => (bound.id === override.id ? BoundaryFacts.change(bound, override) : bound));
                    break;
                }
            }
        }
        return patched;
    }
    static diff(expected, actual, signals) {
        const unpairedActual = [...actual];
        const pairs = [];
        const take = (predicate) => {
            const index = unpairedActual.findIndex(predicate);
            if (index === -1) {
                return null;
            }
            const [taken] = unpairedActual.splice(index, 1);
            return taken ?? null;
        };
        for (const bound of expected) {
            pairs.push({ expected: bound, actual: take((candidate) => candidate.id === bound.id) });
        }
        for (const pair of pairs) {
            if (pair.actual !== null) {
                continue;
            }
            const shape = BoundaryFacts.shapeKey(pair.expected);
            const placement = BoundaryFacts.placementKey(pair.expected);
            pair.actual =
                take((candidate) => BoundaryFacts.shapeKey(candidate) === shape && BoundaryFacts.placementKey(candidate) === placement) ?? take((candidate) => BoundaryFacts.shapeKey(candidate) === shape);
        }
        const overrides = [];
        for (const pair of pairs) {
            if (pair.actual === null) {
                overrides.push({ op: ExpressionExpansion_1.BoundOverrideOp.Remove, id: pair.expected.id });
                continue;
            }
            const changes = BoundaryFacts.changes(pair.expected, pair.actual);
            if (BoundaryFacts.equal(BoundaryFacts.apply([pair.expected], changes), [pair.actual], signals)) {
                overrides.push(...changes);
                continue;
            }
            overrides.push({ op: ExpressionExpansion_1.BoundOverrideOp.Remove, id: pair.expected.id }, { op: ExpressionExpansion_1.BoundOverrideOp.Add, bound: pair.actual });
        }
        for (const bound of unpairedActual) {
            overrides.push({ op: ExpressionExpansion_1.BoundOverrideOp.Add, bound });
        }
        return overrides;
    }
    static limits(numerical) {
        const nominal = numerical.type !== 'absolute' && numerical.nominalValue !== undefined ?
            [{ slot: 'typ', value: numerical.nominalValue, inclusive: true }]
            : [];
        switch (numerical.type) {
            case 'minimum':
                return [{ slot: 'min', value: numerical.value, inclusive: numerical.valInclusive ?? true }, ...nominal];
            case 'maximum':
                return [...nominal, { slot: 'max', value: numerical.value, inclusive: numerical.valInclusive ?? true }];
            case 'range':
                return [
                    { slot: 'min', value: numerical.minVal, inclusive: numerical.minValIncluded ?? true },
                    ...nominal,
                    { slot: 'max', value: numerical.maxVal, inclusive: numerical.maxValIncluded ?? true },
                ];
            case 'absolute':
                return [{ slot: 'typ', value: numerical.value, inclusive: true }];
        }
    }
    static conditionsKey(bound) {
        return (bound.conditions ?? [])
            .map((condition) => {
            if (sigma_engine_shared_1.DesignModels.isModeReferenceCondition(condition)) {
                const mode = BoundaryFacts.escapeKeyEntry(condition.mode);
                return condition.mode_state ? mode : `!${mode}`;
            }
            return BoundaryFacts.escapeKeyEntry(JSON.stringify(condition));
        })
            .sort()
            .join(',');
    }
    static escapeKeyEntry(entry) {
        return entry.replace(/%/g, '%25').replace(/,/g, '%2C').replace(/!/g, '%21');
    }
    static change(bound, override) {
        const definition = bound.boundary;
        if (!sigma_engine_shared_1.DesignModels.isBoundaryNumericalDefinition(definition)) {
            return bound;
        }
        const numerical = override.slot === undefined ?
            definition.numerical
            : BoundaryFacts.changeSlot(definition.numerical, override.slot, override.value, override.inclusive);
        const { relative_to: _relative, ...withoutRelative } = definition;
        const relative = override.relativeSignalId === undefined ? definition.relative_to
            : override.relativeSignalId === null ? undefined
                : { signal: override.relativeSignalId };
        const [onlySignalId, ...moreSignalIds] = override.signalIds ?? [];
        const target = override.signalIds === undefined ? definition.target
            : onlySignalId === undefined ? { component: true }
                : moreSignalIds.length === 0 ? { signal: onlySignalId }
                    : { signal: override.signalIds };
        return {
            ...bound,
            boundary: {
                ...withoutRelative,
                ...(relative === undefined ? {} : { relative_to: relative }),
                target,
                ...(override.level === undefined ? {} : { level: override.level }),
                numerical,
            },
        };
    }
    static changeSlot(numerical, slot, value, inclusive) {
        if (slot === 'typ') {
            if (value === undefined) {
                return numerical;
            }
            return numerical.type === 'absolute' ? { ...numerical, value } : { ...numerical, nominalValue: value };
        }
        switch (numerical.type) {
            case 'range': {
                if (slot === 'min') {
                    const { minValIncluded, ...rest } = { ...numerical, minVal: value ?? numerical.minVal };
                    const open = inclusive === undefined ? minValIncluded
                        : inclusive ? undefined
                            : false;
                    return open === undefined ? rest : { ...rest, minValIncluded: open };
                }
                const { maxValIncluded, ...rest } = { ...numerical, maxVal: value ?? numerical.maxVal };
                const open = inclusive === undefined ? maxValIncluded
                    : inclusive ? undefined
                        : false;
                return open === undefined ? rest : { ...rest, maxValIncluded: open };
            }
            case 'minimum':
            case 'maximum': {
                if ((numerical.type === 'minimum') !== (slot === 'min')) {
                    return numerical;
                }
                const { valInclusive, ...rest } = { ...numerical, value: value ?? numerical.value };
                const open = inclusive === undefined ? valInclusive
                    : inclusive ? undefined
                        : false;
                return open === undefined ? rest : { ...rest, valInclusive: open };
            }
            case 'absolute':
                return numerical;
        }
    }
    static changes(expected, actual) {
        const expectedDefinition = expected.boundary;
        const actualDefinition = actual.boundary;
        if (!sigma_engine_shared_1.DesignModels.isBoundaryNumericalDefinition(expectedDefinition) ||
            !sigma_engine_shared_1.DesignModels.isBoundaryNumericalDefinition(actualDefinition)) {
            return [];
        }
        const changes = [];
        const expectedLimits = BoundaryFacts.limits(expectedDefinition.numerical);
        for (const actualLimit of BoundaryFacts.limits(actualDefinition.numerical)) {
            const expectedLimit = expectedLimits.find((limit) => limit.slot === actualLimit.slot);
            const valueChanged = expectedLimit === undefined || !BoundaryFacts.sameValue(expectedLimit.value, actualLimit.value);
            const inclusiveChanged = expectedLimit === undefined || expectedLimit.inclusive !== actualLimit.inclusive;
            if (!valueChanged && !inclusiveChanged) {
                continue;
            }
            changes.push({
                op: ExpressionExpansion_1.BoundOverrideOp.Change,
                id: expected.id,
                slot: actualLimit.slot,
                ...(valueChanged ? { value: actualLimit.value } : {}),
                ...(inclusiveChanged ? { inclusive: actualLimit.inclusive } : {}),
            });
        }
        changes.sort((a, b) => SLOT_ORDER.indexOf(a.slot ?? 'min') - SLOT_ORDER.indexOf(b.slot ?? 'min'));
        const expectedRelative = BoundaryFacts.relativeOf(expectedDefinition.relative_to);
        const actualRelative = BoundaryFacts.relativeOf(actualDefinition.relative_to);
        if (expectedRelative !== actualRelative) {
            changes.push({ op: ExpressionExpansion_1.BoundOverrideOp.Change, id: expected.id, relativeSignalId: actualRelative });
        }
        const expectedTargets = BoundaryFacts.targetKey(expectedDefinition.target);
        const actualTargets = BoundaryFacts.targetKey(actualDefinition.target);
        if (expectedTargets !== actualTargets) {
            const signalIds = (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)([], actualDefinition.target).map(String);
            changes.push({ op: ExpressionExpansion_1.BoundOverrideOp.Change, id: expected.id, signalIds });
        }
        if (expectedDefinition.level !== actualDefinition.level && actualDefinition.level !== undefined) {
            changes.push({ op: ExpressionExpansion_1.BoundOverrideOp.Change, id: expected.id, level: actualDefinition.level });
        }
        return changes;
    }
    static shapeKey(bound) {
        const definition = bound.boundary;
        if (!sigma_engine_shared_1.DesignModels.isBoundaryNumericalDefinition(definition)) {
            return JSON.stringify(bound.boundary);
        }
        const { numerical } = definition;
        return JSON.stringify([
            definition.class ?? null,
            definition.strength ?? null,
            numerical.type,
            numerical.characteristic,
            numerical.type === 'range' ? (numerical.rangeMode ?? null) : null,
            numerical.variance ?? null,
            numerical.useAbsVal ?? null,
            numerical.type !== 'absolute' && numerical.nominalValue !== undefined,
            BoundaryFacts.conditionsKey(bound),
        ]);
    }
    static placementKey(bound) {
        return JSON.stringify([
            BoundaryFacts.targetKey(bound.boundary.target),
            BoundaryFacts.relativeOf(bound.boundary.relative_to),
        ]);
    }
    static targetKey(target) {
        if ('component' in target) {
            return '*';
        }
        return JSON.stringify((0, sigma_engine_shared_1.getBoundaryTargetSignalIds)([], target).map(String).sort());
    }
    static relativeOf(relative) {
        const [first] = (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)([], relative);
        return first === undefined ? null : String(first);
    }
    static sorted(facts) {
        return [...facts].sort((a, b) => {
            const partsA = BoundaryFacts.identityParts(a);
            const partsB = BoundaryFacts.identityParts(b);
            for (const [index, partA] of partsA.entries()) {
                const partB = partsB[index] ?? '';
                if (partA !== partB) {
                    return partA < partB ? -1 : 1;
                }
            }
            return a.value - b.value;
        });
    }
    static distinct(facts) {
        const distinct = [];
        for (const fact of BoundaryFacts.sorted([...facts])) {
            const previous = distinct[distinct.length - 1];
            if (previous === undefined || !BoundaryFacts.sameFact(previous, fact)) {
                distinct.push(fact);
            }
        }
        return distinct;
    }
    static identity(fact) {
        return JSON.stringify(BoundaryFacts.identityParts(fact));
    }
    static identityParts(fact) {
        return [
            fact.signalId ?? '',
            fact.characteristic,
            String(SLOT_ORDER.indexOf(fact.slot)),
            fact.relativeSignalId ?? '',
            fact.conditionsKey,
            fact.inclusive ? '1' : '0',
            fact.level ?? '',
        ];
    }
    static sameFact(a, b) {
        return (b !== null && BoundaryFacts.identity(a) === BoundaryFacts.identity(b) && BoundaryFacts.sameValue(a.value, b.value));
    }
    static sameValue(a, b) {
        return Math.abs(a - b) <= BoundaryFacts.RELATIVE_EPSILON * Math.max(Math.abs(a), Math.abs(b));
    }
    static isRated(characteristic) {
        return sigma_engine_shared_1.RatedBoundaryCharacteristics.includes(characteristic);
    }
}
exports.BoundaryFacts = BoundaryFacts;
//# sourceMappingURL=boundary-facts.js.map